import React from 'react';
import {
  Paper,
  Box,
  Typography,
  Divider,
  Alert,
  Skeleton
} from '@mui/material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import CustomButton from '../../../components/buttons/CustomButton';
import ColorPalette from '../../../colorpalette/ColorPalette';

const CONFIRM_DISABLE_OWN_NUMBER =
  'Confirmações, avisos e lembretes deixam de sair sozinhos e voltam para o WhatsApp Web, com o texto pronto. O chat, o envio de documentos e o envio em massa deixam de ficar disponíveis. Suas credenciais continuam salvas. Deseja continuar?';

const cardSx = {
  backgroundColor: ColorPalette.white,
  borderRadius: '12px',
  padding: '24px',
  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.03)',
  border: `1px solid ${ColorPalette.border}`,
  mb: 3
};

const headlineSx = {
  fontWeight: 600,
  color: ColorPalette.textDark,
  fontSize: '1.05rem',
  lineHeight: 1.45
};

/**
 * Card 1 — "Por onde suas mensagens saem".
 *
 * Só informa o estado atual e oferece as ações de alto nível (desligar / ativar o número
 * próprio). Toda a configuração — credenciais, pareamento — vive no card de passos logo
 * abaixo, em Whatsapp.js. Desde 2026-10-23 nada ao paciente sai pelo número da Gabriela:
 * sem número próprio ativo, tudo sai pelo WhatsApp Web com a mensagem pronta.
 *
 * @param {Object} props
 * @param {boolean} props.loading Status ainda carregando (WhatsAppSenderContext)
 * @param {string|null} props.state platform | own_connected | own_disabled | own_unavailable
 * @param {Object} props.own Bloco `own` de get_sender_status.php
 * @param {Object} props.platform Bloco `platform` de get_sender_status.php (sem uso na tela)
 * @param {string|null} props.phoneNumber Telefone do dispositivo conectado, quando conhecido
 * @param {boolean} props.ownConnected Instância própria conectada agora (passo 2 concluído)
 * @param {boolean} props.saving Toggle de use_own_number em andamento
 * @param {Function} props.onUsePlatformNumber Desliga use_own_number
 * @param {Function} props.onActivateOwnNumber Liga use_own_number
 * @param {Function} props.onManageInstance Abre/rola o card de passos
 * @param {Function} props.onReconnect Abre o card de passos no passo "Conectar o WhatsApp"
 * @param {boolean} [props.ownNumberAllowed] Plano inclui número próprio
 */
function SenderOriginCard({
  loading,
  state,
  phoneNumber,
  ownConnected,
  saving,
  onUsePlatformNumber,
  onActivateOwnNumber,
  onManageInstance,
  onReconnect,
  ownNumberAllowed = true
}) {
  if (loading) {
    return (
      <Paper elevation={0} sx={cardSx}>
        <Skeleton variant="text" width="45%" height={32} />
        <Skeleton variant="text" width="85%" />
        <Skeleton variant="rectangular" height={40} sx={{ mt: 2, borderRadius: 1 }} />
      </Paper>
    );
  }

  if (!state) {
    return null;
  }

  const handleDisableOwnNumber = () => {
    if (window.confirm(CONFIRM_DISABLE_OWN_NUMBER)) {
      onUsePlatformNumber();
    }
  };

  const renderBody = () => {
    if (state === 'own_connected') {
      return (
        <>
          <Typography sx={headlineSx}>
            Suas mensagens saem pelo seu número
            {phoneNumber ? ` (${phoneNumber})` : ''}.
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            Confirmações, avisos e lembretes saem sozinhos. Chat, envio de documentos e envio em
            massa estão liberados.
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 2.5 }}>
            <CustomButton
              variant="outlined"
              style="secondary"
              label={saving ? 'Salvando...' : 'Desligar meu número'}
              onClick={handleDisableOwnNumber}
              disabled={saving}
            />
            <CustomButton
              variant="text"
              style="secondary"
              label="Gerenciar instância"
              onClick={onManageInstance}
            />
          </Box>
        </>
      );
    }

    if (state === 'own_unavailable') {
      return (
        <>
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography sx={{ fontWeight: 600 }}>
              Seu número está desconectado.
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              As mensagens automáticas NÃO estão sendo enviadas e ficam aguardando. Reconecte seu
              número para voltar a enviar.
            </Typography>
          </Alert>
          <CustomButton
            variant="contained"
            style="secondary"
            label="Reconectar"
            onClick={onReconnect}
          />
        </>
      );
    }

    if (state === 'own_disabled') {
      return (
        <>
          <Typography sx={headlineSx}>
            Suas mensagens saem pelo WhatsApp Web, com o texto pronto.
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            Você tem um número próprio configurado e desligado. Ative-o para as mensagens saírem
            sozinhas pelo sistema.
          </Typography>
          <Box sx={{ mt: 2.5 }}>
            {ownConnected ? (
              <CustomButton
                variant="contained"
                style="secondary"
                label={saving ? 'Ativando...' : 'Ativar meu número'}
                onClick={onActivateOwnNumber}
                disabled={saving}
              />
            ) : (
              <Typography variant="body2" color="textSecondary">
                Conecte seu número no card abaixo para poder ativar.
              </Typography>
            )}
          </Box>
        </>
      );
    }

    // state === 'platform': sem número próprio configurado
    if (!ownNumberAllowed) {
      return (
        <>
          <Typography sx={headlineSx}>
            Neste plano as mensagens saem pelo WhatsApp Web, com o texto pronto e um clique por
            envio.
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            O envio pelo sistema, com o seu próprio número, está disponível a partir do plano
            Profissional.
          </Typography>
        </>
      );
    }

    return (
      <>
        <Typography sx={headlineSx}>
          Suas mensagens saem pelo WhatsApp Web, com o texto pronto e um clique por envio.
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
          Conecte o seu próprio número abaixo para confirmações, avisos e lembretes saírem
          sozinhos, e para liberar chat, documentos e envio em massa.
        </Typography>
      </>
    );
  };

  return (
    <Paper elevation={0} sx={cardSx}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <WhatsAppIcon sx={{ color: ColorPalette.secondary, mr: 1 }} />
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Por onde suas mensagens saem
        </Typography>
      </Box>

      <Divider sx={{ my: 2 }} />

      {renderBody()}
    </Paper>
  );
}

export default SenderOriginCard;
